import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { FileText, ArrowLeft } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoicesListPage() {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/invoices`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setInvoices(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const statusColor = (s) => ({
    pending: 'bg-amber-50 text-amber-700',
    confirmed: 'bg-secondary text-primary',
    completed: 'bg-emerald-50 text-emerald-700',
    cancelled: 'bg-destructive/10 text-destructive',
  }[s] || 'bg-secondary text-muted-foreground');

  return (
    <div className="min-h-screen bg-background" data-testid="invoices-list-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-6 min-h-[44px]">
          <ArrowLeft size={16} /> Back to Dashboard
        </Link>
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight text-foreground mb-8">Invoices</h1>

        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-secondary/70 rounded-xl animate-pulse" />)}</div>
        ) : invoices.length === 0 ? (
          <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-12 text-center fade-up">
            <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
              <FileText size={26} className="text-primary" />
            </div>
            <p className="text-sm text-muted-foreground mb-4">No invoices yet — they'll appear here after your first booking.</p>
            <Link to="/browse" className="inline-flex items-center justify-center bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer">Browse services</Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="invoices-table">
            {/* Column labels (desktop) */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-5 text-[11px] uppercase tracking-widest text-muted-foreground font-medium">
              <span className="col-span-3">Invoice</span>
              <span className="col-span-3">Provider</span>
              <span className="col-span-2">Date</span>
              <span className="col-span-2">Amount</span>
              <span className="col-span-2 text-right">Status</span>
            </div>
            {invoices.map((inv, i) => (
              <Link
                key={inv.booking_id}
                to={`/invoices/${inv.booking_id}`}
                data-testid={`view-invoice-${inv.booking_id}`}
                className="grid grid-cols-2 md:grid-cols-12 items-center gap-2 md:gap-4 bg-card rounded-xl border border-border/70 shadow-soft px-5 py-4 min-h-[44px] hover:bg-secondary/50 hover:shadow-lift transition-all duration-300 cursor-pointer fade-up"
                style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}
              >
                <span className="col-span-2 md:col-span-3 flex items-center gap-2 font-mono text-xs text-muted-foreground">
                  <FileText size={15} className="text-primary shrink-0" /> {inv.invoice_number}
                </span>
                <span className="md:col-span-3 text-sm font-medium text-foreground truncate">{inv.provider_name}</span>
                <span className="md:col-span-2 text-sm text-muted-foreground">{inv.date ? new Date(inv.date).toLocaleDateString() : '—'}</span>
                <span className="md:col-span-2 text-sm font-semibold text-foreground tabular-nums">${inv.total}</span>
                <span className="md:col-span-2 flex md:justify-end">
                  <span className={`rounded-full text-xs font-medium px-3 py-1 capitalize ${statusColor(inv.status)}`}>{inv.status}</span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
